import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  bomConfigVersionId,
  type BomConfigVersionId,
} from './BomConfigVersion.js'
import { bomRoutingId, type BomRoutingId } from './BomRouting.js'

/** Identifier type for public.bom_mapping_ */
export type BomMappingId = string & { __brand: 'BomMappingId' }

/** Represents the table public.bom_mapping_ */
export default interface BomMappingTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<BomMappingId, BomMappingId | undefined, BomMappingId>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.uuid */
  versionId: ColumnType<
    BomConfigVersionId,
    BomConfigVersionId,
    BomConfigVersionId
  >

  /** Database type: pg_catalog.text */
  key: ColumnType<string, string, string>

  /** Database type: pg_catalog.uuid */
  routingId: ColumnType<BomRoutingId, BomRoutingId, BomRoutingId>

  /** Database type: pg_catalog.text[] */
  subassemblies: ColumnType<string[] | null, string[] | null, string[] | null>

  /** Database type: pg_catalog.bool */
  isActive: ColumnType<boolean | null, boolean | null, boolean | null>
}

export type BomMapping = Selectable<BomMappingTable>

export type BomMappingInitializer = Insertable<BomMappingTable>

export type BomMappingMutator = Updateable<BomMappingTable>

export const bomMappingId = z.string() as unknown as z.Schema<BomMappingId>

export const bomMapping = z.object({
  id: bomMappingId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: z.string().nullable().optional(),
  versionId: bomConfigVersionId,
  key: z.string(),
  routingId: bomRoutingId,
  subassemblies: z.string().array().nullable().optional(),
  isActive: z.boolean().nullable().optional(),
}) as unknown as z.Schema<BomMapping>

export const bomMappingInitializer = z.object({
  id: bomMappingId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  versionId: bomConfigVersionId,
  key: z.string(),
  routingId: bomRoutingId,
  subassemblies: z.string().array().optional().nullable(),
  isActive: z.boolean().optional().nullable(),
}) as unknown as z.Schema<BomMappingInitializer>

export const bomMappingMutator = z.object({
  id: bomMappingId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  versionId: bomConfigVersionId.optional(),
  key: z.string().optional(),
  routingId: bomRoutingId.optional(),
  subassemblies: z.string().array().optional().nullable(),
  isActive: z.boolean().optional().nullable(),
}) as unknown as z.Schema<BomMappingMutator>
